import { useEffect, useRef, useState } from 'react';
import Icon from '@/components/public/ui/Icon';
import { pickerTriggerClass } from '@/lib/formStyles';

interface DatePickerProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  hasError?: boolean;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}

const monthNames = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];
const dayHeads = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
const yearBlockSize = 24;

const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
const toISO = (y: number, m: number, d: number) => `${y}-${pad(m + 1)}-${pad(d)}`;
const yearBlockStartFor = (year: number) => Math.floor(year / yearBlockSize) * yearBlockSize;

function parseISO(s: string): { y: number; m: number; d: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s ?? '');
  if (!match) return null;
  return { y: +match[1], m: +match[2] - 1, d: +match[3] };
}

function formatPT(s: string): string {
  const p = parseISO(s);
  return p ? `${pad(p.d)}/${pad(p.m + 1)}/${p.y}` : '';
}

type PickerView = 'day' | 'month' | 'year';

export default function DatePicker({
  id,
  value,
  onChange,
  min,
  max,
  disabled,
  placeholder = 'Selecionar data',
  hasError,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
}: DatePickerProps) {
  const today = new Date();
  const todayISO = toISO(today.getFullYear(), today.getMonth(), today.getDate());
  const initial = parseISO(value) ?? {
    y: today.getFullYear(),
    m: today.getMonth(),
    d: today.getDate(),
  };

  const [viewY, setViewY] = useState(initial.y);
  const [viewM, setViewM] = useState(initial.m);
  const [pickerView, setPickerView] = useState<PickerView>('day');
  const [yearBlockStart, setYearBlockStart] = useState(yearBlockStartFor(initial.y));
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const openPopover = () => {
    const p = parseISO(value);
    if (p) {
      setViewY(p.y);
      setViewM(p.m);
      setYearBlockStart(yearBlockStartFor(p.y));
    }
    setPickerView('day');
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const daysInMonth = new Date(viewY, viewM + 1, 0).getDate();
  const firstDow = new Date(viewY, viewM, 1).getDay();
  const leading = (firstDow + 6) % 7;
  const prevMonthDays = new Date(viewY, viewM, 0).getDate();
  const prevMonthDate = new Date(viewY, viewM - 1, 1);
  const nextMonthDate = new Date(viewY, viewM + 1, 1);

  type Cell = { y: number; m: number; d: number; muted: boolean };
  const cells: Cell[] = [];
  for (let i = leading; i > 0; i--) {
    cells.push({
      y: prevMonthDate.getFullYear(),
      m: prevMonthDate.getMonth(),
      d: prevMonthDays - i + 1,
      muted: true,
    });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ y: viewY, m: viewM, d, muted: false });
  }
  const trailing = (7 - (cells.length % 7)) % 7;
  for (let d = 1; d <= trailing; d++) {
    cells.push({ y: nextMonthDate.getFullYear(), m: nextMonthDate.getMonth(), d, muted: true });
  }

  const isOutOfRange = (iso: string) => (!!min && iso < min) || (!!max && iso > max);

  const isMonthOutOfRange = (year: number, month: number) => {
    const first = toISO(year, month, 1);
    const last = toISO(year, month, new Date(year, month + 1, 0).getDate());
    return (!!min && last < min) || (!!max && first > max);
  };

  const isYearOutOfRange = (year: number) => {
    const first = toISO(year, 0, 1);
    const last = toISO(year, 11, 31);
    return (!!min && last < min) || (!!max && first > max);
  };

  const nearestAvailableMonth = (year: number, preferredMonth: number) => {
    if (!isMonthOutOfRange(year, preferredMonth)) return preferredMonth;
    const available = monthNames.findIndex((_, month) => !isMonthOutOfRange(year, month));
    return available >= 0 ? available : preferredMonth;
  };

  const goPrev = () => {
    if (pickerView === 'year') {
      setYearBlockStart(yearBlockStart - yearBlockSize);
      return;
    }

    if (pickerView === 'month') {
      setViewY(viewY - 1);
      return;
    }

    if (viewM === 0) {
      setViewM(11);
      setViewY(viewY - 1);
    } else {
      setViewM(viewM - 1);
    }
  };
  const goNext = () => {
    if (pickerView === 'year') {
      setYearBlockStart(yearBlockStart + yearBlockSize);
      return;
    }

    if (pickerView === 'month') {
      setViewY(viewY + 1);
      return;
    }

    if (viewM === 11) {
      setViewM(0);
      setViewY(viewY + 1);
    } else {
      setViewM(viewM + 1);
    }
  };

  const firstOfView = toISO(viewY, viewM, 1);
  const lastOfView = toISO(viewY, viewM, daysInMonth);
  const minYear = parseISO(min ?? '')?.y;
  const maxYear = parseISO(max ?? '')?.y;
  const prevDisabled =
    pickerView === 'year'
      ? minYear !== undefined && yearBlockStart <= minYear
      : pickerView === 'month'
        ? isYearOutOfRange(viewY - 1)
        : !!min && firstOfView <= min;
  const nextDisabled =
    pickerView === 'year'
      ? maxYear !== undefined && yearBlockStart + yearBlockSize - 1 >= maxYear
      : pickerView === 'month'
        ? isYearOutOfRange(viewY + 1)
        : !!max && lastOfView >= max;
  const todayDisabled = isOutOfRange(todayISO);

  const goToday = () => {
    if (todayDisabled) return;
    setViewY(today.getFullYear());
    setViewM(today.getMonth());
    setYearBlockStart(yearBlockStartFor(today.getFullYear()));
    setPickerView('day');
    onChange(todayISO);
    setOpen(false);
  };

  const clear = () => {
    onChange('');
    setPickerView('day');
    setOpen(false);
  };

  const openYearView = () => {
    setYearBlockStart(yearBlockStartFor(viewY));
    setPickerView('year');
  };

  const chooseMonth = (month: number) => {
    if (isMonthOutOfRange(viewY, month)) return;
    setViewM(month);
    setPickerView('day');
  };

  const chooseYear = (year: number) => {
    if (isYearOutOfRange(year)) return;
    setViewY(year);
    setViewM(nearestAvailableMonth(year, viewM));
    setYearBlockStart(yearBlockStartFor(year));
    setPickerView('day');
  };

  const yearBlockYears = Array.from({ length: yearBlockSize }, (_, i) => yearBlockStart + i);
  const yearRangeLabel = `${yearBlockStart}-${yearBlockStart + yearBlockSize - 1}`;

  const triggerClasses = pickerTriggerClass({ open, hasError, disabled });

  const display = formatPT(value);

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        id={id}
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          if (open) setOpen(false);
          else openPopover();
        }}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
        className={triggerClasses}
      >
        <span className={display ? '' : 'text-on-surface-light/60'}>{display || placeholder}</span>
        <Icon name="calendar_month" size="sm" className="text-on-surface-light flex-shrink-0" />
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Selecionar data"
          className="surface-card-elevated absolute left-0 z-50 mt-2 w-80 max-w-[calc(100vw-2rem)] p-4"
        >
          <div className="mb-3 flex items-center justify-between">
            <div className="border-line bg-background inline-flex h-10 items-center overflow-hidden rounded-md border">
              <button
                type="button"
                onClick={goPrev}
                disabled={prevDisabled}
                className="text-primary/85 hover:bg-primary/10 disabled:text-primary/25 flex h-full w-10 items-center justify-center transition-colors duration-200 disabled:hover:bg-transparent"
                aria-label={
                  pickerView === 'year'
                    ? 'Bloco de anos anterior'
                    : pickerView === 'month'
                      ? 'Ano anterior'
                      : 'Mês anterior'
                }
              >
                <Icon name="chevron_left" size="sm" />
              </button>
              <div className="bg-line h-5 w-px flex-shrink-0" />
              {pickerView === 'day' && (
                <span className="font-headline text-primary flex min-w-[130px] items-center justify-center gap-1 px-2 text-sm font-bold select-none">
                  <button
                    type="button"
                    onClick={() => setPickerView('month')}
                    className="hover:bg-primary/10 rounded px-1.5 py-1 transition-colors"
                  >
                    {monthNames[viewM]}
                  </button>
                  <button
                    type="button"
                    onClick={openYearView}
                    className="hover:bg-primary/10 rounded px-1.5 py-1 transition-colors"
                  >
                    {viewY}
                  </button>
                </span>
              )}
              {pickerView === 'month' && (
                <button
                  type="button"
                  onClick={openYearView}
                  className="font-headline text-primary hover:bg-primary/10 min-w-[130px] px-2 text-center text-sm font-bold transition-colors"
                >
                  {viewY}
                </button>
              )}
              {pickerView === 'year' && (
                <span className="font-headline text-primary min-w-[130px] px-2 text-center text-sm font-bold select-none">
                  {yearRangeLabel}
                </span>
              )}
              <div className="bg-line h-5 w-px flex-shrink-0" />
              <button
                type="button"
                onClick={goNext}
                disabled={nextDisabled}
                className="text-primary/85 hover:bg-primary/10 disabled:text-primary/25 flex h-full w-10 items-center justify-center transition-colors duration-200 disabled:hover:bg-transparent"
                aria-label={
                  pickerView === 'year'
                    ? 'Próximo bloco de anos'
                    : pickerView === 'month'
                      ? 'Próximo ano'
                      : 'Próximo mês'
                }
              >
                <Icon name="chevron_right" size="sm" />
              </button>
            </div>
            <button
              type="button"
              onClick={goToday}
              disabled={todayDisabled}
              className="text-on-surface-light hover:bg-primary/5 hover:text-primary rounded-md px-2 py-1 text-xs font-semibold transition-colors disabled:opacity-30"
            >
              Hoje
            </button>
          </div>

          {pickerView === 'day' && (
            <>
              <div className="mb-1 grid grid-cols-7 gap-0.5">
                {dayHeads.map((d, i) => (
                  <div
                    key={i}
                    className="text-on-surface-light/55 py-1.5 text-center text-[0.65rem] font-bold tracking-wider uppercase"
                  >
                    {d}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-0.5">
                {cells.map((c, i) => {
                  const iso = toISO(c.y, c.m, c.d);
                  const selected = iso === value;
                  const isToday = iso === todayISO;
                  const dis = isOutOfRange(iso);
                  return (
                    <button
                      key={i}
                      type="button"
                      disabled={dis}
                      onClick={() => {
                        onChange(iso);
                        setOpen(false);
                      }}
                      aria-pressed={selected}
                      aria-label={`${c.d} de ${monthNames[c.m]} de ${c.y}`}
                      className={[
                        'relative flex h-9 items-center justify-center rounded-lg border border-transparent text-sm transition-all duration-150',
                        c.muted ? 'text-on-surface-light/30' : 'text-on-surface',
                        selected
                          ? 'bg-primary font-bold text-white'
                          : isToday
                            ? 'text-primary font-bold'
                            : 'font-medium',
                        !selected && !dis ? 'hover:border-primary/15 hover:bg-primary/10' : '',
                        dis ? 'cursor-not-allowed opacity-30' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {c.d}
                      {isToday && !selected && (
                        <span className="bg-primary absolute bottom-1 h-1 w-1 rounded-full" />
                      )}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {pickerView === 'month' && (
            <div className="grid grid-cols-3 gap-2 py-1">
              {monthNames.map((month, monthIndex) => {
                const selected = monthIndex === viewM;
                const dis = isMonthOutOfRange(viewY, monthIndex);
                return (
                  <button
                    key={month}
                    type="button"
                    disabled={dis}
                    onClick={() => chooseMonth(monthIndex)}
                    aria-pressed={selected}
                    className={[
                      'rounded-lg border border-transparent px-2 py-3 text-sm font-semibold transition-all duration-150',
                      selected ? 'bg-primary text-white' : 'text-on-surface',
                      !selected && !dis ? 'hover:border-primary/15 hover:bg-primary/10' : '',
                      dis ? 'cursor-not-allowed opacity-30' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {month.slice(0, 3)}
                  </button>
                );
              })}
            </div>
          )}

          {pickerView === 'year' && (
            <div className="grid grid-cols-4 gap-1.5 py-1">
              {yearBlockYears.map((year) => {
                const selected = year === viewY;
                const dis = isYearOutOfRange(year);
                return (
                  <button
                    key={year}
                    type="button"
                    disabled={dis}
                    onClick={() => chooseYear(year)}
                    aria-pressed={selected}
                    className={[
                      'rounded-lg border border-transparent px-2 py-2.5 text-sm font-semibold transition-all duration-150',
                      selected ? 'bg-primary text-white' : 'text-on-surface',
                      !selected && !dis ? 'hover:border-primary/15 hover:bg-primary/10' : '',
                      dis ? 'cursor-not-allowed opacity-30' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {year}
                  </button>
                );
              })}
            </div>
          )}

          <div className="border-line mt-3 flex items-center justify-between border-t pt-3">
            <button
              type="button"
              onClick={clear}
              disabled={!value}
              className="text-on-surface-light hover:bg-primary/5 hover:text-primary rounded-md px-2 py-1 text-xs font-semibold transition-colors disabled:opacity-30"
            >
              Limpar
            </button>
            <div className="flex items-center gap-1">
              {pickerView !== 'day' && (
                <button
                  type="button"
                  onClick={() => setPickerView('day')}
                  className="text-on-surface-light hover:bg-primary/5 hover:text-primary rounded-md px-2 py-1 text-xs font-semibold transition-colors"
                >
                  Voltar
                </button>
              )}
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-primary hover:bg-primary/10 rounded-md px-2 py-1 text-xs font-semibold transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
